import { groupVariantPatches } from "functions/tlf/groupVariantPatches";
import { isEqual } from "lodash";
import { applySelection } from "./applySelection";
import { getActivePatchResult } from "./getActivePatchResult";
import { getVariantPatchContext } from "./getVariantPatchContext";
import { ITlfActionTrackActiveVariantPatch, TlfContextProps, UPDATE_TLF } from "./interface";
import { tlf_updateTlf } from "./tlf_updateTlf";
import { updateActivePatchErrors } from "./updateActivePatchErrors";

/**
 * Compares the current TLF with the master plus all applied patches except the active patch,
 * then persists supported edits to the active patch's catalogue entry. Tracks invalid operations
 * and reconciles applied patches when the updated patch changes its patch group.
 *
 * @param state - Current TLF context state
 * @param action - Action origin for the history update
 * @returns State with the active patch edits tracked
 */
export const tlf_trackActiveVariantPatch = (
  state: TlfContextProps,
  action: ITlfActionTrackActiveVariantPatch,
): TlfContextProps => {
  const context = getVariantPatchContext(state);
  const result = getActivePatchResult(state, context);
  let newState = state;
  if (
    result !== null &&
    result.invalidOps.length === 0 &&
    context.information !== null &&
    state.activeVariantPatchName !== null &&
    !isEqual(context.information.allVariantPatches[state.activeVariantPatchName], result.patch)
  ) {
    const activeName = state.activeVariantPatchName;
    const allVariantPatches = {
      ...context.information.allVariantPatches,
      [activeName]: result.patch,
    };
    newState = tlf_updateTlf(state, {
      operation: UPDATE_TLF,
      tlfData: {
        allVariantPatches,
      },
      calledFrom: action.calledFrom,
      doNotAddUndo: true,
    });
    const activeGroup = groupVariantPatches({
      ...context.information,
      allVariantPatches,
    })
      .filter((group) => group.includes(activeName))
      .flat();
    const appliedPatchNames = state.appliedVariantPatches.filter(
      (name) => name === activeName || !activeGroup.includes(name),
    );
    if (appliedPatchNames.length !== state.appliedVariantPatches.length) {
      newState = applySelection(
        newState,
        appliedPatchNames,
        allVariantPatches,
        activeName,
        action.calledFrom,
      );
    }
  }
  return updateActivePatchErrors(newState);
};
